/**
 * webFetch — cross-origin fetch wrapper for the Web Player.
 *
 * The native apps (Android ExoPlayer, Electron+mpv) don't enforce CORS,
 * so the player codebase calls provider URLs, EPG XML feeds, TMDB
 * posters and channel-logo CDNs with plain `fetch(url)`. In a browser
 * tab those requests get blocked by CORS.
 *
 * This wrapper routes cross-origin requests through the backend
 * `/api/proxy/fetch?url=…` endpoint when we're running in Web Player
 * mode. Native / same-origin requests fall through unchanged so the
 * Android and Windows apps see zero behavior change.
 *
 * Auth: the proxy endpoint requires an activated device, so we
 * automatically forward the current device's `X-Device-ID` +
 * `X-Activation-Key` headers.
 */
import { API_BASE } from "./playerApi";
import { getDeviceId, detectPlatform } from "./deviceId";
import { getStoredActivationKey } from "./playerApi";
import { APP_VERSION } from "./version";

// True when the current tab is a Web Player session (browser tab that
// opted-in via `/webplayer` or `/admin/webplayer`, or the Windows Web
// Player shell that behaves like a browser). Native Android / Electron
// wrappers have their own IPC and never hit this path.
function isWebPlayerMode() {
  if (typeof window === "undefined") return false;
  if (window.__ZAPP_IS_WEBPLAYER__ === true) return true;
  // Fallback: no native APIs present AND not the Electron shell.
  const hasElectron = !!window.electronAPI?.isElectron;
  const hasCapacitor = !!window.Capacitor?.isNativePlatform?.();
  if (hasElectron || hasCapacitor) return false;
  try {
    return localStorage.getItem("zapp_webplayer_enabled") === "1";
  } catch {
    return false;
  }
}

// v1.4.9 — True when running inside the Windows Web Player Electron
// kiosk (which launches Chromium with `webSecurity: false`). In that
// mode CORS + mixed-content are already disabled by the shell, so we
// MUST NOT proxy fetches through the admin server — that would make
// every stream request originate from the admin IP and blow through
// the provider's max-connections cap on the license.
//
// Direct fetches from the shell hit the provider with the user's real
// IP, exactly like the native Android / mpv paths.
function isWindowsWebPlayerShell() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  const ua = navigator.userAgent || "";
  return ua.includes("ZAWebPlayerWindows/");
}

// Same-origin URL — no proxy needed.
function isSameOrigin(url) {
  if (typeof window === "undefined") return true;
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.origin === window.location.origin;
  } catch {
    // Relative / malformed URLs are same-origin by definition.
    return true;
  }
}

// Wrap a target URL so it hits the backend proxy instead.
function buildProxyUrl(target) {
  return `${API_BASE}/api/proxy/fetch?url=${encodeURIComponent(target)}`;
}

function buildAuthHeaders(userHeaders) {
  const headers = { ...(userHeaders || {}) };
  const did = getDeviceId();
  const key = getStoredActivationKey();
  if (did) headers["X-Device-ID"] = did;
  if (key) headers["X-Activation-Key"] = key;
  headers["X-Platform"] = detectPlatform();
  headers["X-App-Version"] = APP_VERSION;
  return headers;
}

/**
 * Drop-in replacement for `fetch()`. In Web Player mode any cross-origin
 * request is routed through the backend proxy with device auth attached.
 */
export async function webFetch(url, init = {}) {
  if (!url) throw new Error("webFetch: url is required");

  const targetIsAbsolute = /^https?:\/\//i.test(url);
  // v1.4.9 — Skip the proxy when inside the Windows Web Player shell.
  // That shell disables CORS/mixed-content locally so streams can hit
  // the provider from the USER'S IP (correct connection counting),
  // not from the admin server IP.
  if (isWindowsWebPlayerShell()) {
    return fetch(url, init);
  }
  const needsProxy = isWebPlayerMode() && targetIsAbsolute && !isSameOrigin(url);

  if (!needsProxy) {
    return fetch(url, init);
  }

  const proxyUrl = buildProxyUrl(url);
  const mergedInit = {
    ...init,
    method: init.method || "GET",
    headers: buildAuthHeaders(init.headers),
    credentials: "include",
  };
  return fetch(proxyUrl, mergedInit);
}

/**
 * URL rewriter — for `<img src>` and hls.js custom loader.
 * Returns the original URL unchanged in native / same-origin /
 * Windows-Web-Player-shell cases.
 */
export function webUrl(url) {
  if (!url) return url;
  const targetIsAbsolute = /^https?:\/\//i.test(url);
  if (isWindowsWebPlayerShell()) return url;
  if (!(isWebPlayerMode() && targetIsAbsolute && !isSameOrigin(url))) {
    return url;
  }
  const did = getDeviceId();
  const key = getStoredActivationKey();
  const qs = new URLSearchParams({ url });
  if (did) qs.set("device_id", did);
  if (key) qs.set("activation_key", key);
  return `${API_BASE}/api/proxy/fetch?${qs.toString()}`;
}